import React, { useCallback, useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link, useParams } from "react-router-dom";
import {
  MdModeEdit,
  MdOutlineDeleteOutline,
  MdOutlineReceiptLong,
  MdOutlineFileUpload,
  MdOutlineLock,
} from "react-icons/md";
import { toast } from "react-toastify";
import * as expenseService from "../../services/expenseService";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import Loader from "../../components/Loader/Loader";
import { getVendorCurrency } from "../../utils/currency";
import { formatDateHyphenated } from "../../utils/dateFormat";
import IndustryGate from "./IndustryGate";
import UploadExpenseDrawer from "./UploadExpenseDrawer";
import ExpenseEntryDrawer from "./ExpenseEntryDrawer";
import ExpenseDatePicker from "./ExpenseDatePicker";
import { JC_ALQURE, TEMPLATE_COLUMNS, resolveTemplate } from "./expenseTemplates";
import InvoiceCustomerDialog from "./InvoiceCustomerDialog";
import InvoiceCancelDialog from "../../components/Invoice/InvoiceCancelDialog";
import {
  thStyle,
  tdStyle,
  cardStyle,
  labelStyle,
  iconBtnStyle,
  badgeStyle,
  todayYmd,
  describeServices,
} from "./expenseStyles";

// Same look as the other grids, with tighter horizontal padding: this table has 13 columns
// and its dates no longer wrap, so the default 16px would push Actions off-screen.
const gridTh = { ...thStyle, padding: "12px 10px" };
const gridTd = { ...tdStyle, padding: "12px 10px" };

const PAGE_SIZE_OPTIONS = [50, 100, 300];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const STANDARD_COLUMNS = ["Job Date", "Trucking Date", "VIN", "Model", "Classification", "Services", "Net", "VAT", "Total", "Status", "Actions"];
const money = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : "0.00");

// One branch's jobs. The branch comes from the route (/expense-management/branch/:centerId,
// one sidebar menu per branch), so there is no branch dropdown here; the parent keys this
// component by centerId so switching branch starts with clean filters, selection and page.
function ExpensesGridContent({ centerId }) {
  const currency = getVendorCurrency();

  const [centers, setCenters] = useState([]);
  const [centersLoaded, setCentersLoaded] = useState(false);
  // Job Schedule Date filter defaults to today.
  const [dateFrom, setDateFrom] = useState(todayYmd);
  const [dateTo, setDateTo] = useState(todayYmd);
  const [invoicedFilter, setInvoicedFilter] = useState("all");
  const [vinInput, setVinInput] = useState("");
  const [vinSearch, setVinSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [data, setData] = useState({ rows: [], total: 0, totals: { net: 0, vat: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  // Set only via "Select all N matching these filters" - holds every id under
  // the current filters (not just this page), with server-side totals, since
  // the corresponding rows aren't loaded. Cleared by any per-row toggle,
  // filter change or page change so it never gets out of sync with `selected`.
  const [matchingSelection, setMatchingSelection] = useState(null);
  const [matchingLoading, setMatchingLoading] = useState(false);

  const [showUpload, setShowUpload] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [invoiceList, setInvoiceList] = useState(null); // [{id, number}] when a selection spans several invoices

  const loadCenters = useCallback(async () => {
    try {
      const res = await expenseService.getCenters();
      setCenters(res?.success && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.message || "Could not load branches");
    } finally {
      setCentersLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadCenters();
  }, [loadCenters]);

  useEffect(() => {
    const t = setTimeout(() => {
      setVinSearch(vinInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [vinInput]);

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await expenseService.getEntries({
        expense_center_id: centerId,
        job_schedule_date_from: dateFrom,
        job_schedule_date_to: dateTo,
        invoiced: invoicedFilter === "all" ? "" : invoicedFilter,
        vin_number: vinSearch,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      if (res?.success) setData(res.data);
      setSelected(new Set());
      setMatchingSelection(null);
    } catch (err) {
      toast.error(err.message || "Could not load expense entries");
    } finally {
      setLoading(false);
    }
  }, [centerId, dateFrom, dateTo, invoicedFilter, vinSearch, page, pageSize]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const branch = useMemo(() => centers.find((c) => c.id === centerId) || null, [centers, centerId]);
  const branchActive = branch?.status === "ACTIVE";
  // Branches on a customer template show that template's columns; STANDARD keeps the original grid.
  const template = resolveTemplate(branch?.upload_template);
  const templateColumns = TEMPLATE_COLUMNS[template] || null;
  const COLUMNS = templateColumns ? [...templateColumns.map((c) => c.label), "Status", "Actions"] : STANDARD_COLUMNS;

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const changePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const resetFilters = () => {
    setDateFrom(todayYmd());
    setDateTo(todayYmd());
    setInvoicedFilter("all");
    setVinInput("");
    setVinSearch("");
    setPage(1);
  };

  /* ---------------------------- selection ---------------------------- */
  const rows = data.rows;
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const toggleRow = (id) => {
    setMatchingSelection(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setMatchingSelection(null);
    setSelected(allOnPageSelected ? new Set() : new Set(rows.map((r) => r.id)));
  };
  const clearSelection = () => {
    setSelected(new Set());
    setMatchingSelection(null);
  };

  // "Select all N matching these filters" offers itself once every row on the
  // page is selected and there is more beyond the page to select.
  const canSelectAllMatching = !matchingSelection && allOnPageSelected && data.total > rows.length;

  const handleSelectAllMatching = async () => {
    setMatchingLoading(true);
    try {
      const res = await expenseService.getEntryIds({
        expense_center_id: centerId,
        job_schedule_date_from: dateFrom,
        job_schedule_date_to: dateTo,
        invoiced: invoicedFilter === "all" ? "" : invoicedFilter,
        vin_number: vinSearch,
      });
      if (res?.success) {
        setMatchingSelection({ ids: res.data.ids, totals: res.data.totals, pendingCount: res.data.pending_count });
      } else {
        toast.error(expenseService.responseMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Could not load matching entries");
    } finally {
      setMatchingLoading(false);
    }
  };

  // In "matching" mode the rows beyond this page aren't loaded, so sums and
  // invoice status come from the server aggregate returned by getEntryIds.
  const selectionCount = matchingSelection ? matchingSelection.ids.length : selectedRows.length;
  const selNet = matchingSelection ? matchingSelection.totals.net : selectedRows.reduce((s, r) => s + Number(r.net_amount), 0);
  const selTotal = matchingSelection ? matchingSelection.totals.total : selectedRows.reduce((s, r) => s + Number(r.total_amount), 0);
  const allPending = matchingSelection
    ? matchingSelection.ids.length > 0 && matchingSelection.pendingCount === matchingSelection.ids.length
    : selectedRows.length > 0 && selectedRows.every((r) => !r.invoice_id);
  const allInvoiced = !matchingSelection && selectedRows.length > 0 && selectedRows.every((r) => r.invoice_id);
  const canGenerate = allPending;
  // Invoiced entries are locked, so only the not-yet-invoiced part of the selection can be deleted.
  const deletableCount = matchingSelection ? matchingSelection.pendingCount : selectedRows.filter((r) => !r.invoice_id).length;
  const lockedCount = selectionCount - deletableCount;

  const generateHint = selectionCount && !allPending ? "Some selected entries are already invoiced" : "";

  /* ----------------------------- actions ----------------------------- */
  // Generate Invoice first asks for the invoice date and the customer it is raised against.
  const handleGenerate = async (customer, invoiceDate) => {
    if (!canGenerate || !customer) return;
    setGenerating(true);
    try {
      const entryIds = matchingSelection ? matchingSelection.ids : selectedRows.map((r) => r.id);
      await expenseService.generateInvoice(entryIds, undefined, customer, invoiceDate);
      toast.success("Invoice generated and downloaded");
      setShowCustomerDialog(false);
      loadEntries();
    } catch (err) {
      toast.error(expenseService.errorMessage(err, "Could not generate the invoice"));
    } finally {
      setGenerating(false);
    }
  };

  // A blank tab is opened synchronously (inside the click) and pointed at the
  // PDF once its URL arrives - opening after an await would be popup-blocked.
  const openInvoice = async (invoiceId) => {
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    try {
      const res = await expenseService.getInvoice(invoiceId);
      const url = res?.data?.pdf_url;
      if (!res?.success || !url) throw new Error(expenseService.responseMessage(res, "The invoice PDF is not available"));
      if (win) win.location.href = url;
      else window.open(url, "_blank");
    } catch (err) {
      if (win) win.close();
      toast.error(err.message || "Could not open the invoice");
    }
  };

  // Cancel Invoice: an invoice covers every job on it, so impacts are loaded per
  // distinct invoice behind the selection and the dialog flags the jobs that were
  // not selected. Cancelling frees all of them to be invoiced again.
  const loadCancelImpacts = async () => {
    const invoiceIds = [...new Set(selectedRows.map((r) => r.invoice_id))];
    const results = await Promise.all(invoiceIds.map((id) => expenseService.getInvoiceImpact(id)));
    return results.map((res) => {
      if (!res?.success) throw new Error(expenseService.responseMessage(res, "Could not load the invoice details"));
      const { invoice, entries } = res.data;
      return {
        id: invoice.id,
        number: invoice.invoice_number,
        currency: invoice.currency || currency,
        hasPayments: false,
        items: entries.map((e) => ({
          key: e.id,
          label: e.vin_number || e.description || `Job #${e.id}`,
          detail: [e.model, e.job_schedule_date ? formatDateHyphenated(e.job_schedule_date) : null].filter(Boolean).join(" - "),
          amount: e.total_amount,
        })),
      };
    });
  };

  const handleCancelInvoices = async (reason, impacts) => {
    for (const imp of impacts) {
      const res = await expenseService.cancelInvoice(imp.id, reason);
      if (!res?.success) throw new Error(expenseService.responseMessage(res, `Could not cancel ${imp.number}`));
    }
    toast.success(impacts.length > 1 ? "Invoices cancelled" : `Invoice ${impacts[0].number} cancelled`);
    setShowCancelDialog(false);
    clearSelection();
    loadEntries();
  };

  const handleViewSelected = () => {
    const invoices = new Map();
    selectedRows.forEach((r) => invoices.set(r.invoice_id, r.invoice_number));
    if (invoices.size === 1) {
      openInvoice([...invoices.keys()][0]);
    } else {
      setInvoiceList([...invoices.entries()].map(([id, number]) => ({ id, number })));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await expenseService.deleteEntry(deleteEntry.id);
      if (res?.success) {
        toast.success(res.message || "Deleted");
        setDeleteEntry(null);
        loadEntries();
      } else {
        toast.error(expenseService.responseMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = matchingSelection ? matchingSelection.ids : selectedRows.map((r) => r.id);
      const res = await expenseService.bulkDeleteEntries(ids);
      if (res?.success) {
        const { deleted, skipped } = res.data;
        toast.success(`Deleted ${deleted} entr${deleted === 1 ? "y" : "ies"}`);
        if (skipped > 0) toast.warn(`${skipped} skipped because ${skipped === 1 ? "it is" : "they are"} already invoiced`);
        setShowBulkDelete(false);
        loadEntries();
      } else {
        toast.error(expenseService.responseMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setBulkDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 fw-bold" style={{ color: "#111827" }}>
            {branch?.name || "Jobs"}
          </h5>
          <p className="mb-0" style={{ fontSize: "13px", color: "#6B7280" }}>
            Upload job spreadsheets for this branch and generate tax invoices from them.
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="btn btn-danger d-inline-flex align-items-center gap-1"
          style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600, height: "38px" }}
          disabled={!branchActive}
          title={branchActive ? "" : "This branch is not active"}
        >
          <MdOutlineFileUpload size={17} /> Upload Spreadsheet
        </button>
      </div>

      {centersLoaded && !branch && (
        <div className="alert alert-warning py-2" style={{ fontSize: "13px" }}>
          This branch was not found. Pick a branch from the Branch Jobs menu, or add one in <Link to="/expense-management/settings">Setup</Link>.
        </div>
      )}
      {branch && !branchActive && (
        <div className="alert alert-warning py-2" style={{ fontSize: "13px" }}>
          This branch is inactive. Activate it in <Link to="/expense-management/settings">Setup</Link> to upload jobs.
        </div>
      )}

      {/* Filters */}
      <div className="d-flex gap-3 mb-3 align-items-end flex-wrap">
        <div>
          <label htmlFor="expense-filter-date-from" style={labelStyle}>Job Schedule Date From</label>
          <div style={{ width: "160px" }}>
            <ExpenseDatePicker id="expense-filter-date-from" compact isClearable value={dateFrom} max={dateTo} onChange={changeFilter(setDateFrom)} />
          </div>
        </div>
        <div>
          <label htmlFor="expense-filter-date-to" style={labelStyle}>To</label>
          <div style={{ width: "160px" }}>
            <ExpenseDatePicker id="expense-filter-date-to" compact isClearable value={dateTo} min={dateFrom} onChange={changeFilter(setDateTo)} />
          </div>
        </div>
        <div style={{ width: "160px" }}>
          <label style={labelStyle}>Invoice Status</label>
          <AppDropdown
            value={invoicedFilter}
            options={[
              { label: "All", value: "all" },
              { label: "Not invoiced", value: "no" },
              { label: "Invoiced", value: "yes" },
            ]}
            onChange={(e) => changeFilter(setInvoicedFilter)(e.value)}
            style={{ height: "38px", fontSize: "13px" }}
          />
        </div>
        <div style={{ width: "200px" }}>
          <label style={labelStyle}>{template === JC_ALQURE ? "Vehicle No" : "VIN"}</label>
          <input
            type="text"
            className="form-control"
            style={{ height: "38px", fontSize: "13px", borderRadius: "6px" }}
            placeholder={template === JC_ALQURE ? "Search vehicle no" : "Search VIN"}
            value={vinInput}
            onChange={(e) => setVinInput(e.target.value)}
          />
        </div>
        <button className="btn btn-outline-secondary" style={{ height: "38px", fontSize: "13px", borderRadius: "6px" }} onClick={resetFilters}>
          Reset
        </button>
      </div>

      {/* Selection action bar */}
      {selectionCount > 0 && (
        <div className="d-flex flex-column gap-2 mb-3">
          {canSelectAllMatching && (
            <div
              className="d-flex align-items-center justify-content-between flex-wrap gap-2"
              style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "10px", padding: "8px 16px", fontSize: "13px", color: "#1E3A8A" }}
            >
              <span>All {rows.length} on this page are selected.</span>
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ fontSize: "13px", fontWeight: 600, textDecoration: "underline", color: "#1E3A8A" }}
                onClick={handleSelectAllMatching}
                disabled={matchingLoading}
              >
                {matchingLoading ? "Loading…" : `Select all ${data.total} matching these filters`}
              </button>
            </div>
          )}
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2"
            style={{ background: "#FDF2F5", border: "1px solid #F5C6D3", borderRadius: "10px", padding: "10px 16px" }}
          >
            <div style={{ fontSize: "14px", color: "#374151" }}>
              <strong>{selectionCount}</strong> selected &middot; Net {currency} {money(selNet)} &middot; Total {currency} {money(selTotal)}
              {generateHint && <span style={{ color: "#B45309", marginLeft: 12, fontSize: "12px" }}>{generateHint}</span>}
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}
                onClick={clearSelection}
              >
                Clear
              </button>
              <button
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
                style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}
                onClick={handleViewSelected}
                disabled={!allInvoiced}
                title={allInvoiced ? "" : "Select invoiced entries to view their invoice"}
              >
                <MdOutlineReceiptLong size={16} /> View Invoice
              </button>
              <button
                className="btn btn-outline-danger d-inline-flex align-items-center gap-1"
                style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}
                onClick={() => setShowCancelDialog(true)}
                disabled={!allInvoiced}
                title={allInvoiced ? "" : "Select invoiced entries to cancel their invoice"}
              >
                Cancel Invoice
              </button>
              <button
                className="btn btn-outline-danger d-inline-flex align-items-center gap-1"
                style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}
                onClick={() => setShowBulkDelete(true)}
                disabled={deletableCount === 0}
                title={deletableCount === 0 ? "Invoiced entries are locked and cannot be deleted" : ""}
              >
                <MdOutlineDeleteOutline size={16} /> Delete{deletableCount > 0 ? ` (${deletableCount})` : ""}
              </button>
              <button
                className="btn btn-danger d-inline-flex align-items-center gap-1"
                style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}
                onClick={() => setShowCustomerDialog(true)}
                disabled={!canGenerate || generating}
                title={generateHint}
              >
                Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div style={cardStyle}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...gridTh, width: 40 }}>
                  <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll} aria-label="Select all entries on this page" />
                </th>
                {COLUMNS.map((h) => (
                  <th key={h} style={gridTh}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(6)
                  .fill(null)
                  .map((_, i) => (
                    <tr key={i}>
                      {Array(COLUMNS.length + 1)
                        .fill(null)
                        .map((__, j) => (
                          <td key={j} style={gridTd}>
                            <Skeleton width="80%" />
                          </td>
                        ))}
                    </tr>
                  ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} style={{ ...gridTd, textAlign: "center", color: "#6B7280", padding: "48px" }}>
                    No expense entries for these filters
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = selected.has(r.id) ? "#FDF2F5" : "transparent")}
                    style={{ transition: "background 0.15s", background: selected.has(r.id) ? "#FDF2F5" : "transparent" }}
                  >
                    <td style={gridTd}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleRow(r.id)} aria-label={`Select ${r.vin_number}`} />
                    </td>
                    {templateColumns ? (
                      templateColumns.map((c) => (
                        <td key={c.label} style={{ ...gridTd, ...(c.nowrap ? { whiteSpace: "nowrap" } : {}), ...(c.bold ? { fontWeight: 600 } : {}) }}>
                          {c.get(r)}
                        </td>
                      ))
                    ) : (
                      <>
                        <td style={{ ...gridTd, whiteSpace: "nowrap" }}>{formatDateHyphenated(r.job_schedule_date)}</td>
                        <td style={{ ...gridTd, whiteSpace: "nowrap" }}>{formatDateHyphenated(r.trucking_date)}</td>
                        <td style={{ ...gridTd, fontWeight: 500 }}>{r.vin_number}</td>
                        <td style={gridTd}>{r.model || "—"}</td>
                        <td style={gridTd}>{r.classification || "—"}</td>
                        <td style={gridTd}>{describeServices(r)}</td>
                        <td style={gridTd}>{money(r.net_amount)}</td>
                        <td style={gridTd}>
                          {money(r.vat_amount)} <span style={{ color: "#6B7280", fontSize: "12px" }}>({Number(r.vat_pct)}%)</span>
                        </td>
                        <td style={{ ...gridTd, fontWeight: 600 }}>{money(r.total_amount)}</td>
                      </>
                    )}
                    <td style={gridTd}>
                      {r.invoice_id ? (
                        <span style={badgeStyle("#DCFCE7", "#166534")} title="Locked - already invoiced">
                          {r.invoice_number}
                        </span>
                      ) : (
                        <span style={badgeStyle("#FEF3C7", "#92400E")}>Not invoiced</span>
                      )}
                    </td>
                    <td style={gridTd}>
                      {r.invoice_id ? (
                        <>
                          <button style={iconBtnStyle} title="View invoice" onClick={() => openInvoice(r.invoice_id)}>
                            <MdOutlineReceiptLong size={18} />
                          </button>
                          <span title="Locked - already invoiced" style={{ color: "#9CA3AF", marginLeft: 4 }}>
                            <MdOutlineLock size={16} />
                          </span>
                        </>
                      ) : (
                        <>
                          <button style={iconBtnStyle} title="Edit" onClick={() => setEditEntry(r)}>
                            <MdModeEdit size={17} />
                          </button>
                          <button style={iconBtnStyle} title="Delete" onClick={() => setDeleteEntry(r)}>
                            <MdOutlineDeleteOutline size={18} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontSize: "13px", color: "#6B7280" }}>
          <div>
            {data.total} entr{data.total === 1 ? "y" : "ies"} &middot; Net {currency} {money(data.totals.net)} &middot; VAT {currency} {money(data.totals.vat)} &middot; Total{" "}
            <strong style={{ color: "#111827" }}>
              {currency} {money(data.totals.total)}
            </strong>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="expense-page-size" className="mb-0">
              Rows per page
            </label>
            <select id="expense-page-size" className="form-select form-select-sm" style={{ width: "auto" }} value={pageSize} onChange={changePageSize}>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
              Next
            </button>
          </div>
        </div>
      </div>

      <UploadExpenseDrawer
        show={showUpload}
        onHide={() => setShowUpload(false)}
        centerId={branchActive ? centerId : null}
        branchName={branch?.name}
        template={template}
        onUploaded={() => {
          setShowUpload(false);
          loadEntries();
        }}
      />

      <InvoiceCustomerDialog
        show={showCustomerDialog}
        onHide={() => setShowCustomerDialog(false)}
        onConfirm={handleGenerate}
        busy={generating}
        entryCount={selectionCount}
      />

      <InvoiceCancelDialog
        show={showCancelDialog}
        onHide={() => setShowCancelDialog(false)}
        loadImpacts={loadCancelImpacts}
        selectedKeys={new Set(selectedRows.map((r) => r.id))}
        noun="job"
        onConfirm={handleCancelInvoices}
      />

      <ExpenseEntryDrawer
        entry={editEntry}
        onHide={() => setEditEntry(null)}
        onSaved={() => {
          setEditEntry(null);
          loadEntries();
        }}
      />

      <SlidePanel show={!!deleteEntry} onHide={() => !deleting && setDeleteEntry(null)} title="Delete Expense Entry">
        <p style={{ fontSize: "15px", color: "#374151" }}>
          You are about to delete the entry for {template === JC_ALQURE ? "vehicle" : "VIN"} <strong>{deleteEntry?.vin_number}</strong> ({formatDateHyphenated(deleteEntry?.job_schedule_date)}). Are you sure you want to proceed?
        </p>
        <div className="d-flex gap-3 mt-4">
          <button className="btn btn-outline-secondary px-4" onClick={() => setDeleteEntry(null)} disabled={deleting}>
            Cancel
          </button>
          <button className="btn Button primaryBackground text-white px-4" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Loader style={{ width: 40, height: 20 }} /> : "Delete"}
          </button>
        </div>
      </SlidePanel>

      <SlidePanel show={showBulkDelete} onHide={() => !bulkDeleting && setShowBulkDelete(false)} title="Delete Selected Entries">
        <p style={{ fontSize: "15px", color: "#374151" }}>
          You are about to delete <strong>{deletableCount}</strong> expense entr{deletableCount === 1 ? "y" : "ies"}. Are you sure you want to proceed?
        </p>
        {lockedCount > 0 && (
          <p style={{ fontSize: "13px", color: "#B45309" }}>
            {lockedCount} selected {lockedCount === 1 ? "entry is" : "entries are"} already invoiced and will be kept.
          </p>
        )}
        <div className="d-flex gap-3 mt-4">
          <button className="btn btn-outline-secondary px-4" onClick={() => setShowBulkDelete(false)} disabled={bulkDeleting}>
            Cancel
          </button>
          <button className="btn Button primaryBackground text-white px-4" onClick={handleBulkDelete} disabled={bulkDeleting}>
            {bulkDeleting ? <Loader style={{ width: 40, height: 20 }} /> : "Delete"}
          </button>
        </div>
      </SlidePanel>

      <SlidePanel show={!!invoiceList} onHide={() => setInvoiceList(null)} title="Invoices for selected entries">
        <p style={{ fontSize: "14px", color: "#374151" }}>The selected entries belong to several invoices. Open each one below.</p>
        <div className="d-flex flex-column gap-2">
          {(invoiceList || []).map((inv) => (
            <div key={inv.id} className="d-flex justify-content-between align-items-center" style={{ border: "1px solid #E5E7EB", borderRadius: "8px", padding: "10px 14px" }}>
              <strong style={{ fontSize: "14px" }}>{inv.number}</strong>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => openInvoice(inv.id)}>
                Open PDF
              </button>
            </div>
          ))}
        </div>
      </SlidePanel>
    </div>
  );
}

function ExpensesGrid() {
  const { centerId } = useParams();
  return (
    <IndustryGate>
      <ExpensesGridContent key={centerId} centerId={Number(centerId)} />
    </IndustryGate>
  );
}

export default ExpensesGrid;
